// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  getCirculationData,
  getCollectionOverview,
  DASHBOARD_STALE_TIME,
} from "@libraryadmin/services/api";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";

// Palette from CSS variables — consistent with the design system
const CHART_COLORS = {
  primary: "hsl(var(--chart-1))",
  secondary: "hsl(var(--chart-2))",
  accent: "hsl(var(--chart-3))",
  success: "hsl(var(--chart-4))",
  destructive: "hsl(var(--chart-5))",
};

const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.success,
  CHART_COLORS.accent,
  CHART_COLORS.destructive,
];

export function IssueReceiveChart() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-stats", "circulation"],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: getCirculationData,
  });

  return (
    <div
      className="card-elevated animate-fade-in opacity-0"
      style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}
    >
      <h3 className="mb-4 text-lg font-semibold">Book Circulation</h3>
      <div className="h-[280px]">
        {isLoading ? (
          <div className="flex h-full flex-col justify-end gap-2 pb-4">
            {[60, 80, 50, 70, 90, 65, 75].map((h, i) => (
              <Skeleton
                key={i}
                className="h-full w-10 rounded"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barGap={4}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis
                dataKey="month"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                axisLine={{ stroke: "hsl(var(--border))" }}
              />
              <YAxis
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                axisLine={{ stroke: "hsl(var(--border))" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-md)",
                }}
              />
              <Bar
                dataKey="issued"
                fill={CHART_COLORS.primary}
                radius={[4, 4, 0, 0]}
                name="Issued"
              />
              <Bar
                dataKey="received"
                fill={CHART_COLORS.secondary}
                radius={[4, 4, 0, 0]}
                name="Received"
              />
              <Bar
                dataKey="overdue"
                fill={CHART_COLORS.destructive}
                radius={[4, 4, 0, 0]}
                name="Overdue"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export function CollectionPieChart() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-stats", "collection"],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: getCollectionOverview,
  });

  return (
    <div
      className="card-elevated animate-fade-in opacity-0"
      style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}
    >
      <h3 className="mb-4 text-lg font-semibold">Collection Overview</h3>
      <div className="h-[280px]">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Skeleton className="h-[160px] w-[160px] rounded-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={4}
                dataKey="value"
              >
                {data?.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-md)",
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={value => (
                  <span
                    style={{
                      color: "hsl(var(--foreground))",
                      fontSize: "12px",
                    }}
                  >
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
